export const ADMIN_ROLES = ["system_admin", "business_unit"];
export const CLIENT_ROLE_GROUPS = new Set([
  "super_admin",
  "reseller",
  "solution_provider",
  "bank_admin",
  "sme_user",
]);
export const MODULE_TABLE_HEADERS = ["Module", "Create", "Update", "View", "Delete"];

export const formatRoleLabel = (role = "") =>
  role
    .split("_")
    .map((part) =>
      part.toLowerCase() === "sme" ?
      "SME" :
      part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export const formatPermissionLabel = (permission = "") =>
  permission
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export const normalizeRoleKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

export const normalizeId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    if (value._id) return normalizeId(value._id);
    if (value.id) return normalizeId(value.id);
    if (typeof value.toString === "function") return value.toString();
  }
  return String(value);
};

export const isAudienceAllowed = (audiences, audience) => {
  if (!audience) return true;
  const normalizedAudience = normalizeRoleKey(audience);
  if (!normalizedAudience) return true;
  if (!Array.isArray(audiences) || audiences.length === 0) return true;
  return audiences.map(normalizeRoleKey).includes(normalizedAudience);
};

export const filterModulesByAudience = (modules = [], audience) => {
  if (!audience) return modules;
  return (modules || []).reduce((acc, module) => {
    if (!module || typeof module !== "object") return acc;
    const moduleType = String(module.moduleType || "")
      .trim()
      .toLowerCase();
    const moduleAudiences = module.audiences;
    const subModules = Array.isArray(module.subModule) ? module.subModule : [];
    const filteredSubModules = subModules.filter((sub) => {
      const subAudiences =
        Array.isArray(sub?.audiences) && sub.audiences.length > 0
          ? sub.audiences
          : moduleAudiences;
      return isAudienceAllowed(subAudiences, audience);
    });
    const isModuleAllowed = isAudienceAllowed(moduleAudiences, audience);

    if (moduleType === "parent") {
      if (!isModuleAllowed || filteredSubModules.length === 0) return acc;
      acc.push({ ...module, subModule: filteredSubModules });
      return acc;
    }

    if (!isModuleAllowed) return acc;
    acc.push({ ...module, subModule: filteredSubModules });
    return acc;
  }, []);
};

export const buildPermitAllPermissions = (modules = [], audience) => {
  const source = filterModulesByAudience(modules, audience);
  const mapItem = (item = {}) => {
    const key = item.key || item.id || item._id || item.title || item.name;
    const title =
      item.title || item.name || item.value || item.key || "Permission";
    const subModules = Array.isArray(item.subModule) ? item.subModule : [];
    return {
      key,
      title,
      actions: { create: true, update: true, view: true, delete: true },
      children: subModules.map(mapItem),
    };
  };

  return Array.isArray(source) ? source.map(mapItem) : [];
};

export const buildPermissionRows = (permissions = []) => {
  if (!Array.isArray(permissions) || permissions.length === 0) return [];

  const rows = [];
  const hasActions = (actions = {}) =>
    Object.values(actions || {}).some(Boolean);

  const traverse = (node, parents = []) => {
    if (typeof node === "string") {
      rows.push({
        label: [...parents, formatPermissionLabel(node)].join(" / "),
        actions: { create: true, update: true, view: true, delete: true },
      });
      return true;
    }

    if (!node || typeof node !== "object") return false;

    const title = formatPermissionLabel(
      node.title || node.name || node.value || node.key || "Module",
    );

    const path = [...parents, title];
    const children = Array.isArray(node.children)
      ? node.children
      : Array.isArray(node.subModule)
        ? node.subModule
        : [];

    let childHasPermission = false;
    children.forEach((child) => {
      childHasPermission = traverse(child, path) || childHasPermission;
    });

    const ownPermission = hasActions(node.actions) && children.length === 0;

    if (ownPermission) {
      rows.push({
        label: path.join(" / "),
        actions: {
          create: Boolean(node.actions?.create),
          update: Boolean(node.actions?.update),
          view: Boolean(node.actions?.view),
          delete: Boolean(node.actions?.delete),
        },
      });
    }

    return ownPermission || childHasPermission;
  };

  permissions.forEach((item) => traverse(item, []));
  return rows;
};

export const buildRoleItems = (roles = [], moduleSource = []) => {
  if (!roles.length) return [];
  return roles.map((item) => {
    const role = item.name || item.title || item.key || "Unnamed role";
    const roleKey = normalizeRoleKey(
      item.key || item.name || item.title || item.role,
    );
    const roleGroup = normalizeRoleKey(item.group || roleKey);
    const audience = roleGroup;
    const permitAllPermissions = buildPermitAllPermissions(
      moduleSource,
      audience,
    );
    const isPermitAll = Boolean(item.isPermitAll) || roleKey === "super_admin";
    const basePermissions =
      isPermitAll && permitAllPermissions.length > 0
        ? permitAllPermissions
        : item.permissions;
    const permissions = Array.isArray(basePermissions)
      ? basePermissions.map((perm) => {
          if (
            perm &&
            typeof perm === "object" &&
            (perm.actions || perm.children || perm.subModule)
          ) {
            return perm;
          }
          return perm?.title || perm?.key || perm?.name || "Permission";
        })
      : [];

    return {
      id: item._id || item.id || item.key || item.name || role,
      role,
      permissions,
      raw: item,
    };
  });
};
